/**
 * DJEHOUTY — Capture des identifiants publicitaires Meta au moment de la commande
 * ------------------------------------------------------------------------------
 * Problème résolu :
 *   `_fbc` (identifiant du clic publicitaire Meta) n'existe que dans le navigateur,
 *   au moment de la visite. La confirmation de commande a lieu plus tard, côté
 *   back-office, après un appel téléphonique — il n'y a alors plus ni navigateur,
 *   ni cookie, ni URL.
 *
 * Solution :
 *   On capture `_fbc`, `_fbp` et le User-Agent pendant la navigation, et on les
 *   attache au panier via /cart/update.js. Shopify recopie automatiquement les
 *   attributs de panier dans la commande. Au moment de la confirmation, le
 *   back-office relit ces valeurs sur la commande et les rejoue vers la
 *   Conversions API de Meta.
 *
 * Pourquoi les attributs de panier plutôt que le checkout :
 *   Le checkout n'est pas modifiable sur Shopify Basic. Les attributs de panier
 *   sont le seul canal fiable pour faire voyager une donnée du navigateur
 *   jusqu'à la commande.
 *
 * Convention Shopify : un attribut préfixé par « _ » est stocké sur la commande
 * mais masqué dans l'affichage client. C'est exactement ce qu'on veut.
 *
 * Ce script est défensif : il n'échoue jamais bruyamment et ne peut pas casser
 * la boutique. Toute erreur est avalée silencieusement.
 *
 * Version 1.0 — 15 août 2026
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'dj_fbc_cache';
  var SENT_KEY = 'dj_fb_attrs_sent';
  var RETRY_DELAYS = [0, 1200, 4000]; // le pixel Meta peut poser _fbc avec du retard

  /* ---------------------------------------------------------------- outils */

  function readCookie(name) {
    try {
      var match = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]*)');
      return match ? decodeURIComponent(match.pop()) : null;
    } catch (e) {
      return null;
    }
  }

  function readQueryParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  function safeGet(store, key) {
    try { return window[store].getItem(key); } catch (e) { return null; }
  }

  function safeSet(store, key, value) {
    try { window[store].setItem(key, value); } catch (e) { /* mode privé */ }
  }

  /* ------------------------------------------------------------------ fbc */

  /**
   * Récupère `_fbc`. Trois sources, par ordre de fiabilité :
   *   1. Le cookie _fbc posé par le pixel Meta
   *   2. Reconstruction depuis le paramètre `fbclid` de l'URL d'arrivée
   *      (utile si le pixel n'a pas encore posé le cookie)
   *   3. Le cache local, si la valeur a été captée sur une page précédente
   *
   * Format imposé par Meta : fb.<sous-domaine>.<timestamp_ms>.<fbclid>
   */
  function resolveFbc() {
    var cookie = readCookie('_fbc');
    if (cookie) {
      safeSet('localStorage', STORAGE_KEY, cookie);
      return cookie;
    }

    var fbclid = readQueryParam('fbclid');
    if (fbclid) {
      var rebuilt = 'fb.1.' + Date.now() + '.' + fbclid;
      safeSet('localStorage', STORAGE_KEY, rebuilt);
      return rebuilt;
    }

    return safeGet('localStorage', STORAGE_KEY);
  }

  /* -------------------------------------------------------------- collecte */

  function collect() {
    var attributes = {};

    var fbc = resolveFbc();
    if (fbc) attributes['_fbc'] = fbc;

    var fbp = readCookie('_fbp');
    if (fbp) attributes['_fbp'] = fbp;

    if (navigator && navigator.userAgent) {
      attributes['_user_agent'] = navigator.userAgent;
    }

    return attributes;
  }

  /* ---------------------------------------------------------------- envoi */

  /**
   * N'écrit sur le panier que si les valeurs ont changé depuis le dernier envoi.
   * Évite un appel réseau inutile à chaque page vue.
   */
  function persist(attributes) {
    var keys = Object.keys(attributes);
    if (keys.length === 0) return;

    var signature = JSON.stringify(attributes);
    if (safeGet('sessionStorage', SENT_KEY) === signature) return;

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    })
      .then(function (response) {
        if (response.ok) {
          safeSet('sessionStorage', SENT_KEY, signature);
        }
      })
      .catch(function () {
        /* silencieux : ne doit jamais gêner le parcours d'achat */
      });
  }

  /* --------------------------------------------------------------- cycle */

  function run() {
    try {
      persist(collect());
    } catch (e) {
      /* silencieux */
    }
  }

  function schedule() {
    RETRY_DELAYS.forEach(function (delay) {
      if (delay === 0) { run(); return; }
      window.setTimeout(run, delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }

  // Après un ajout au panier, Shopify peut réécrire le panier : on repasse.
  document.addEventListener('cart:updated', run);
  document.addEventListener('cart:refresh', run);
})();
